/*
  ════════════════════════════════════════════════════════════════════════════
  SAFEPASS DESIGN TOKENS — the shared source

  The web dashboard reads these as CSS custom properties (apps/web/src/index.css
  declares them, tailwind.config.js maps them). React Native cannot read CSS
  variables, so the mobile apps read the same values from here instead. One
  palette, two consumers, no drift.

  Colours are plain hex, because that is what React Native's StyleSheet takes.
  The web side keeps the HSL-channel form so it can use Tailwind's alpha
  modifiers (`bg-success/[0.07]`); the two are the same colours, and
  `docs/DESIGN.md` records which hex maps to which channel triplet.

  RULES THAT TRAVEL WITH THESE VALUES

  · radius.panel (4px) is the product's one corner radius. Nothing is a pill
    except an actual dot.
  · Elevation belongs to overlays only. A card sits on `surface` above
    `canvas` and is separated by a hairline `border`, not a shadow.
  · The semantic five (success / warning / danger / info / neutral) carry
    meaning. They are never used to decorate. On this product a green figure
    says "released", not "this number is pleasant".
  · Status is a mark: a ~7% wash of the tone, a ~25% hairline of the tone, and
    the tone's `ink` for the text. The tone itself is too light to read as
    type on its own wash, which is what `ink` is for.
  ════════════════════════════════════════════════════════════════════════════
*/

/** Light theme. The default everywhere; gate staff work outdoors in daylight. */
export const LIGHT = {
  canvas: '#F4F7FA',
  surface: '#FFFFFF',
  surfaceMuted: '#EEF1F5',
  text: '#101B29',
  textMuted: '#67707E',
  border: '#DBE1E9',

  primary: '#1157D4',
  primaryHover: '#0F49B0',
  onPrimary: '#FFFFFF',

  /** The command bar. Does not flip with the theme — it identifies the product. */
  bar: '#101E32',
  onBar: '#FFFFFF',

  accent: '#E1EBFD',

  success: '#22815A',
  warning: '#E19008',
  danger: '#D42B2B',
  info: '#1288BA',

  inkSuccess: '#146343',
  inkWarning: '#97590F',
  inkDanger: '#AE1F1F',
  inkInfo: '#0E5E84',
  inkNeutral: '#565E6B',
} as const;

/** Dark theme. Tones are lifted, not just inverted — a 45%-lightness blue
 *  fails contrast on a near-black ground. */
export const DARK = {
  canvas: '#0D131C',
  surface: '#161D28',
  surfaceMuted: '#1D2530',
  text: '#E6EBF2',
  textMuted: '#8A94A3',
  border: '#2F3743',

  primary: '#4D94F5',
  primaryHover: '#7AB0F8',
  onPrimary: '#0E1726',

  bar: '#0C1424',
  onBar: '#FFFFFF',

  accent: '#1B2C49',

  success: '#36A173',
  warning: '#EDA332',
  danger: '#D9534F',
  info: '#3FAEDA',

  inkSuccess: '#68C79B',
  inkWarning: '#F2B455',
  inkDanger: '#EF8785',
  inkInfo: '#65C6EB',
  inkNeutral: '#8A94A3',
} as const;

export type Palette = typeof LIGHT;

/** Series colours for charts, ordered so the first two are distinguishable to
 *  a red-green colour blind reader — those are the two a 2-series chart uses. */
export const CHART_SERIES = ['#1157D4', '#2E9168', '#F0A519', '#1B9BD1', '#8A5BD6'] as const;

/* ── Geometry ──────────────────────────────────────────────────────────────── */

export const RADIUS = {
  /** Panels, cards, controls, inputs — everything. */
  panel: 4,
  /** Marks and small chips. */
  mark: 3,
  /** Actual dots only. */
  dot: 999,
} as const;

/** A 4px base step, so vertical rhythm is arithmetic rather than per-screen. */
export const SPACE = { xs: 4, sm: 8, md: 12, lg: 16, xl: 24, xxl: 32 } as const;

/* ── Type ───────────────────────────────────────────────────────────────────
   Deliberately dense on the dashboard, where officers read tables all shift.
   The mobile apps step the body sizes up — a phone held at arm's length at a
   school gate in sunlight is not a laptop on a desk — but they keep the same
   ratios and the same small-caps treatment for labels. */

export const TYPE = {
  /** 9.5 on web; a field name or column label, always uppercase + tracked. */
  label: { size: 10, weight: '600', tracking: 0.8, uppercase: true },
  meta: { size: 12, weight: '400' },
  body: { size: 14, weight: '400' },
  control: { size: 13, weight: '600' },
  sectionTitle: { size: 14, weight: '700' },
  pageTitle: { size: 19, weight: '700' },
  /** The pickup code itself: read aloud and typed under time pressure. */
  code: { size: 34, weight: '700', tracking: 8 },
  figure: { size: 22, weight: '700' },
} as const;

/** The one place elevation is allowed. */
export const OVERLAY_SHADOW = {
  shadowColor: '#080F1A',
  shadowOpacity: 0.28,
  shadowRadius: 24,
  shadowOffset: { width: 0, height: 12 },
  elevation: 12,
} as const;

/* ── Status marks ───────────────────────────────────────────────────────────── */

export type Tone = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

/**
 * The three colours a status mark needs, for a given tone and theme.
 *
 * Returned as 8-digit hex so React Native gets the wash and hairline without
 * needing an alpha prop — RN supports `#RRGGBBAA`.
 */
export function mark(tone: Tone, palette: Palette = LIGHT) {
  const base: Record<Tone, { tint: string; ink: string }> = {
    success: { tint: palette.success, ink: palette.inkSuccess },
    warning: { tint: palette.warning, ink: palette.inkWarning },
    danger: { tint: palette.danger, ink: palette.inkDanger },
    info: { tint: palette.info, ink: palette.inkInfo },
    neutral: { tint: palette.textMuted, ink: palette.inkNeutral },
  };
  const { tint, ink } = base[tone];
  return {
    background: `${tint}14`, //  8% wash
    borderColor: `${tint}40`, // 25% hairline
    color: ink,
  };
}

/** Pickup-event type → the tone it is shown in, so the gate app, the parent app
 *  and the dashboard cannot disagree about what "released" looks like. */
export const EVENT_TONE: Record<string, Tone> = {
  released: 'success',
  failed_attempt: 'danger',
  suspicious_flagged: 'danger',
  code_revoked: 'warning',
  delegate_added: 'info',
  delegate_cancelled: 'warning',
  offline_sync: 'neutral',
};
